// src/hooks/usePartners.js - Hook replacement that uses PartnersStore
import usePartnersStore from '../stores/PartnersStore';
import { useSocket } from '../contexts/SocketContext';

/**
 * Custom hook for accessing partners data from PartnersStore
 * 
 * UPDATED: Now uses Zustand PartnersStore instead of PartnersProvider
 * Store initialization is handled at app level in SocketContext.
 * 
 * FIXED: Uses proper Zustand selectors instead of destructuring entire store
 * 
 * @returns {object} Partners data and operations
 */
export const usePartners = () => {
  const socket = useSocket();
  
  // 🚨 FIXED: Use individual selectors instead of destructuring entire store
  
  // Main partner data
  const connectedPartners = usePartnersStore(state => state.connectedPartners);
  const pendingConnections = usePartnersStore(state => state.pendingConnections);
  const networkProfile = usePartnersStore(state => state.networkProfile);
  const networkStats = usePartnersStore(state => state.networkStats);
  
  // Search and discovery
  const searchResults = usePartnersStore(state => state.searchResults);
  const searchLoading = usePartnersStore(state => state.searchLoading);
  
  // Referral collaborations
  const referralCollaborations = usePartnersStore(state => state.referralCollaborations);
  const selectedCollaboration = usePartnersStore(state => state.selectedCollaboration);
  const collaborationLoading = usePartnersStore(state => state.collaborationLoading);
  
  // Partner availability
  const partnerAvailability = usePartnersStore(state => state.partnerAvailability);
  const availabilityLoading = usePartnersStore(state => state.availabilityLoading);
  
  // Partner services
  const partnerServices = usePartnersStore(state => state.partnerServices);
  const servicesLoading = usePartnersStore(state => state.servicesLoading);
  
  // Loading and error states
  const partnersLoading = usePartnersStore(state => state.partnersLoading);
  const partnersError = usePartnersStore(state => state.partnersError);
  const connectionLoading = usePartnersStore(state => state.connectionLoading);
  
  // Partner network operations (functions don't change, so we can get them once)
  const loadPartnerNetwork = usePartnersStore(state => state.loadPartnerNetwork);
  const updateNetworkProfile = usePartnersStore(state => state.updateNetworkProfile);
  const refreshPartnerNetwork = usePartnersStore(state => state.refreshPartnerNetwork);
  
  // Business discovery operations
  const searchPartnerBusinesses = usePartnersStore(state => state.searchPartnerBusinesses);
  const clearSearchResults = usePartnersStore(state => state.clearSearchResults);
  
  // Connection operations
  const sendConnectionRequest = usePartnersStore(state => state.sendConnectionRequest);
  const respondToConnectionRequest = usePartnersStore(state => state.respondToConnectionRequest);
  
  // Collaboration operations
  const loadReferralCollaborations = usePartnersStore(state => state.loadReferralCollaborations);
  const addReferralNote = usePartnersStore(state => state.addReferralNote);
  const selectCollaboration = usePartnersStore(state => state.selectCollaboration);
  const clearSelectedCollaboration = usePartnersStore(state => state.clearSelectedCollaboration);
  
  // Availability operations
  const loadPartnerAvailability = usePartnersStore(state => state.loadPartnerAvailability);
  
  // Partner Services operations
  const loadPartnerServices = usePartnersStore(state => state.loadPartnerServices);
  
  // Utility functions
  const clearPartnersError = usePartnersStore(state => state.clearPartnersError);
  const getPartnerById = usePartnersStore(state => state.getPartnerById);
  const getCollaborationById = usePartnersStore(state => state.getCollaborationById);
  const getIncomingRequests = usePartnersStore(state => state.getIncomingRequests);
  const getOutgoingRequests = usePartnersStore(state => state.getOutgoingRequests);
  const getActiveCollaborations = usePartnersStore(state => state.getActiveCollaborations);
  
  // Connection state
  const isConnected = usePartnersStore(state => state.isConnected);
  
  // Internal initialization state (for fallback check)
  const _initialized = usePartnersStore(state => state._initialized);
  
  // Fallback initialization check (safety net)
  // If app-level initialization failed but we're authenticated, try fallback
  if (socket.isAuthenticated && !socket.storesInitialized && !_initialized) {
    console.warn('⚠️ usePartners: Store not initialized at app level, trying fallback');
    socket.initializeStore('PartnersStore');
  }
  
  // Return the exact same interface as the old PartnersProvider
  return {
    // Main partner data
    connectedPartners,
    pendingConnections,
    networkProfile,
    networkStats,
    
    // Search and discovery
    searchResults,
    searchLoading,
    
    // Referral collaborations
    referralCollaborations,
    selectedCollaboration,
    collaborationLoading,
    
    // Partner availability
    partnerAvailability,
    availabilityLoading,
    
    // Partner services
    partnerServices,
    servicesLoading,
    
    // Loading and error states
    partnersLoading,
    partnersError,
    connectionLoading,
    
    // Partner network operations
    loadPartnerNetwork,
    updateNetworkProfile,
    refreshPartnerNetwork,
    
    // Business discovery operations
    searchPartnerBusinesses,
    clearSearchResults,
    
    // Connection operations
    sendConnectionRequest,
    respondToConnectionRequest,
    
    // Collaboration operations
    loadReferralCollaborations,
    addReferralNote,
    selectCollaboration,
    clearSelectedCollaboration,
    
    // Availability operations
    loadPartnerAvailability,
    
    // Partner Services operations
    loadPartnerServices,
    
    // Utility functions
    clearPartnersError,
    getPartnerById,
    getCollaborationById,
    getIncomingRequests,
    getOutgoingRequests,
    getActiveCollaborations,
    
    // Connection state
    isConnected: socket.isAuthenticated,
    
    // Cache utilities (legacy compatibility)
    getCachedData: () => null, // Simplified for store approach
    isDataFresh: () => true     // Simplified for store approach
  };
};

export default usePartners;